import React, { useState } from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { Tag } from "component/shared/tag";
import { followUpBossCopy } from "../../follow-up-boss-copy";

interface Props {
    checked: boolean;
    onChange: (checked: boolean) => void;
    // MAX-4028: fixed-seat plans can't add agent seats — toggle renders disabled and
    // OFF with an explanatory tooltip (Figma 1968-25405); no banner, no confirm modal
    lockedOff?: boolean;
    // MAX-4580: reseller accounts see the white-labeled wording. Required rather than optional:
    // a brand gate that defaults to the branded copy fails open, which is the exact leak this
    // ticket exists to prevent.
    whiteLabel: boolean;
}

/**
 * "Auto-create Unmatched Agents" toggle with billing note and turn-off confirmation
 * dialog. Shared between wizard step 2 (Set Sync Preferences) and the Settings tab.
 */
export function AutoCreateToggle({ checked, onChange, lockedOff, whiteLabel }: Props) {
    const copy = followUpBossCopy(whiteLabel);
    const [confirmOpen, setConfirmOpen] = useState(false);

    return (
        <>
            {!lockedOff && (
                /* Design-system warning banner — sits directly above the setting per the
                    Q2 2026 designs (Figma 1417-14669) */
                <Mui.Box display="flex" style={{ gap: 8, backgroundColor: "#FFF9E0", borderRadius: 4, padding: 16, marginBottom: 24 }}>
                    <Icons.AlertTriangle size={16} style={{ color: "#9D6506", flexShrink: 0, marginTop: 4 }} />
                    <Mui.Typography style={{ fontSize: 16, lineHeight: "24px", color: "#1C1B1F" }}>
                        Auto-create Unmatched Agents is required for lead assignments to sync correctly.
                    </Mui.Typography>
                </Mui.Box>
            )}
            <Mui.FormControlLabel
                control={
                    lockedOff ? (
                        <Mui.Tooltip title={copy.autoCreateLockedTooltip} placement="top-start" arrow>
                            {/* span so the tooltip fires on a disabled control */}
                            <span>
                                <Mui.Switch checked={false} disabled color="secondary" />
                            </span>
                        </Mui.Tooltip>
                    ) : (
                        <Mui.Switch
                            checked={checked}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    onChange(true);
                                } else {
                                    setConfirmOpen(true);
                                }
                            }}
                            color="secondary"
                        />
                    )
                }
                style={{ alignItems: "flex-start" }}
                label={
                    <Mui.Box style={{ paddingTop: 6, marginLeft: 4 }}>
                        <Mui.Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: "wrap" }}>
                            <Mui.Typography variant="body2" style={{ fontWeight: 600, color: lockedOff ? "#5F6368" : "#1C1B1F" }}>Auto-create Unmatched Agents</Mui.Typography>
                            {!lockedOff && <Tag label="Recommended" variant="success" size="small" />}
                        </Mui.Box>
                        <Mui.Typography variant="body2" style={{ marginTop: 8, color: "#1C1B1F" }}>
                            {copy.autoCreateDescription}
                        </Mui.Typography>
                    </Mui.Box>
                }
            />
            {/* Design-system confirm modal (Figma 1448-34070): 24px bold title with warning
                icon and close X, inset divider, 18px body, 32px gap to the actions */}
            <Mui.Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <Mui.DialogTitle style={{ padding: "24px 24px 16px" }}>
                    <Mui.Box display="flex" alignItems="center" justifyContent="space-between">
                        <Mui.Box display="flex" alignItems="center" style={{ gap: 8 }}>
                            <Icons.AlertTriangle size={24} style={{ color: "#f57f17" }} />
                            <Mui.Typography style={{ fontSize: 24, fontWeight: 700, color: "#1C1B1F" }}>Turn off Auto-create?</Mui.Typography>
                        </Mui.Box>
                        <Mui.IconButton size="small" onClick={() => setConfirmOpen(false)}>
                            <Icons.X size={20} />
                        </Mui.IconButton>
                    </Mui.Box>
                </Mui.DialogTitle>
                <Mui.Divider style={{ margin: "0 24px" }} />
                <Mui.DialogContent style={{ padding: "16px 24px 0" }}>
                    <Mui.Typography style={{ fontSize: 18, lineHeight: "28px", color: "#1C1B1F" }}>
                        {copy.autoCreateTurnOffLead}<strong>lead assignment will not sync correctly</strong> for those agents.
                    </Mui.Typography>
                </Mui.DialogContent>
                <Mui.DialogActions style={{ padding: "32px 24px 24px" }}>
                    <Mui.Button
                        variant="outlined"
                        color="primary"
                        style={{ padding: "7px 16px" }}
                        onClick={() => {
                            onChange(false);
                            setConfirmOpen(false);
                        }}
                    >
                        Turn Off
                    </Mui.Button>
                    <Mui.Button
                        variant="contained"
                        color="primary"
                        onClick={() => setConfirmOpen(false)}
                    >
                        Cancel
                    </Mui.Button>
                </Mui.DialogActions>
            </Mui.Dialog>
        </>
    );
}
